/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at Sat Feb 03 2024
 **/
/* eslint-disable @typescript-eslint/no-explicit-any */

import { Typography, Row, Button } from "@/frameworks/designStuff/components";
import { _T } from "@rfice/language";
import { Maybe } from "@/frameworks/libraries/components";
import { colors } from "@/frameworks/core/styles";
import * as S from "./emotion";

export function VCIcon() {
	return (
		<S.IconBackground style={{ backgroundColor: colors.accent_50_100 }}>
			<S.StyledIcon name="VoiceChat24" size={16} fill={colors.neutral_00_100} />
		</S.IconBackground>
	);
}

export function VCTitle({ isJoined }: { isJoined: boolean | null }) {
	return (
		<Typography typoColor="neutral_00_100" typoSize="Title3">
			{_T("보이스챗 {{state}}", { state: isJoined ? _T("참여중") : _T("진행중") })}
		</Typography>
	);
}

export function VCInfo({
	members,
	time,
	isRecording,
	onUserCountClicked
}: {
	members: any[];
	time: number;
	isRecording: boolean;
	onUserCountClicked: (ev: React.MouseEvent<HTMLDivElement>) => void;
}) {
	return (
		<>
			<S.UserCountWrapper onClick={onUserCountClicked}>
				<S.StyledIcon name="User24" size={16} />
				<Typography typoColor="accent_80_100" typoSize="Title3">
					{members.length >= 999 ? "999+" : members.length}
				</Typography>
			</S.UserCountWrapper>
			<Maybe test={isRecording}>
				<Row verticalAlign="center" gap={8} css={{ paddingLeft: "8px" }}>
					<Typography typoColor="neutral_00_100" typoSize="Body7">
						{new Date(time * 1000).toISOString().substring(11, 19)}
					</Typography>
					<S.Record>
						<Typography
							typoSize="Body7"
							style={{
								color: "#fff"
							}}
						>
							{_T("REC")}
						</Typography>
					</S.Record>
				</Row>
			</Maybe>
		</>
	);
}

export function VCControlPad({
	micState,
	isJoined,
	isRecording,
	onRecord,
	onMicToggle
}: {
	micState: MediaState;
	isJoined: boolean | null;
	isRecording: boolean;
	onRecord: (ev: React.MouseEvent<HTMLDivElement>) => void;
	onMicToggle: () => void;
}) {
	return (
		<Maybe test={isJoined}>
			<Row gap={8}>
				<S.IconBackground style={{ backgroundColor: colors.accent_20_100 }} onClick={onMicToggle}>
					<S.StyledIcon
						name={micState === "on" ? "MicOn24" : "MicOff24"}
						size={20}
						fill={colors.accent_80_100}
					/>
				</S.IconBackground>
				<S.IconBackground
					style={{ backgroundColor: colors.accent_20_100 }}
					id={isRecording ? "stop" : "start"}
					onClick={onRecord}
				>
					<S.StyledIcon
						fill={isRecording ? "#181D8D" : "#ff4848"}
						name={isRecording ? "Stop24" : "RecordUnique24"}
						size={20}
					/>
				</S.IconBackground>
			</Row>
		</Maybe>
	);
}

export function VCStartButton({
	chatSize,
	isJoined,
	isLoading,
	onStart,
	onExit,
	numberOfMembers
}: {
	chatSize: "mini" | "full";
	isJoined: boolean;
	isLoading: boolean | null;
	onStart: () => void;
	onExit: () => void;
	numberOfMembers?: number;
}) {
	const calculateBottom = () => {
		let px;

		if (chatSize === "mini") {
			px = numberOfMembers === 1 ? (isJoined === false ? "30px" : "45px") : "12px";
		} else {
			px = numberOfMembers === 1 ? (isJoined === false ? "35px" : "50px") : "15px";
		}

		if (!px) {
			px = "12px";
		}

		return px;
	};

	return (
		<Row
			style={{
				height: "100%",
				alignItems: "end",
				position: "absolute",
				bottom: calculateBottom(),
				right: "16px"
			}}
		>
			<Maybe
				test={isJoined === false}
				fallback={
					<>
						<S.VerticalDivider />
						<S.IconBackground style={{ backgroundColor: colors.accent_20_100 }}>
							<S.StyledIcon name="Exit24" size={20} onClick={onExit} />
						</S.IconBackground>
					</>
				}
			>
				<Button size="small" style={{ width: "65px" }} onClick={onStart} disabled={isLoading === null}>
					{_T("시작")}
				</Button>
			</Maybe>
		</Row>
	);
}
